// Conflict detection — three kinds:
// 1. Official overbooking: same official assigned to overlapping games
// 2. Surface overlap: two non-break games on same surface overlapping
// 3. Game during break: a game scheduled on a surface while a break is on that surface

function overlap(a, b) {
  // a and b have start_time, end_time and same date
  if ((a.date || "") !== (b.date || "")) return false;
  if (!a.start_time || !a.end_time || !b.start_time || !b.end_time) return false;
  return a.start_time < b.end_time && b.start_time < a.end_time;
}

export function findConflicts(games) {
  const conflictsByGameRole = {}; // key: gameId|role -> array of gameId|role
  const conflictsByOfficial = {}; // officialId -> array of gameId|role
  const gamesInSurfaceConflict = new Set();
  const gamesInBreakConflict = new Set();
  const gamesInDuplicate = new Set();
  const gamesInOfficialConflict = new Set();
  const surfaceConflictDetails = {};

  const roles = ["referee1_id", "referee2_id", "scorer_id"];

  // 0. Exact duplicates: same date + surface + start_time + end_time on multiple non-break games
  const dupGroups = {};
  games.forEach((g) => {
    if (g.is_break) return;
    if (!g.start_time || !g.end_time) return;
    const k = `${g.date || ""}|${String(g.surface)}|${g.start_time}|${g.end_time}`;
    (dupGroups[k] = dupGroups[k] || []).push(g.id);
  });
  Object.values(dupGroups).forEach((arr) => {
    if (arr.length > 1) arr.forEach((id) => gamesInDuplicate.add(id));
  });

  // 1. Official overbooking
  // 1a. INTRA-game : same official assigned twice in the same game (referee1==referee2, etc.)
  games.forEach((g) => {
    if (g.is_break) return;
    for (let r1 = 0; r1 < roles.length; r1++) {
      const oa = g[roles[r1]];
      if (!oa) continue;
      for (let r2 = r1 + 1; r2 < roles.length; r2++) {
        if (oa === g[roles[r2]]) {
          const ka = `${g.id}|${roles[r1]}`;
          const kb = `${g.id}|${roles[r2]}`;
          if (!conflictsByGameRole[ka]) conflictsByGameRole[ka] = [];
          if (!conflictsByGameRole[kb]) conflictsByGameRole[kb] = [];
          conflictsByGameRole[ka].push(kb);
          conflictsByGameRole[kb].push(ka);
          if (!conflictsByOfficial[oa]) conflictsByOfficial[oa] = [];
          conflictsByOfficial[oa].push(ka, kb);
          gamesInOfficialConflict.add(g.id);
        }
      }
    }
  });

  // 1b. INTER-game : same official in two overlapping games
  for (let i = 0; i < games.length; i++) {
    const a = games[i];
    if (a.is_break) continue;
    for (let j = i + 1; j < games.length; j++) {
      const b = games[j];
      if (b.is_break) continue;
      if (!overlap(a, b)) continue;
      for (const ra of roles) {
        if (!a[ra]) continue;
        for (const rb of roles) {
          if (a[ra] === b[rb]) {
            const ka = `${a.id}|${ra}`;
            const kb = `${b.id}|${rb}`;
            if (!conflictsByGameRole[ka]) conflictsByGameRole[ka] = [];
            if (!conflictsByGameRole[kb]) conflictsByGameRole[kb] = [];
            conflictsByGameRole[ka].push(kb);
            conflictsByGameRole[kb].push(ka);
            if (!conflictsByOfficial[a[ra]]) conflictsByOfficial[a[ra]] = [];
            conflictsByOfficial[a[ra]].push(ka, kb);
            gamesInOfficialConflict.add(a.id);
            gamesInOfficialConflict.add(b.id);
          }
        }
      }
    }
  }

  // 2. Surface overlap (game vs game on same surface)
  const sameSurfaceGroups = {};
  games.forEach((g) => {
    if (g.is_break) return;
    const k = `${g.date || ""}::${g.surface}`;
    (sameSurfaceGroups[k] = sameSurfaceGroups[k] || []).push(g);
  });
  Object.values(sameSurfaceGroups).forEach((arr) => {
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        if (overlap(arr[i], arr[j])) {
          gamesInSurfaceConflict.add(arr[i].id);
          gamesInSurfaceConflict.add(arr[j].id);
          (surfaceConflictDetails[arr[i].id] = surfaceConflictDetails[arr[i].id] || []).push(arr[j]);
          (surfaceConflictDetails[arr[j].id] = surfaceConflictDetails[arr[j].id] || []).push(arr[i]);
        }
      }
    }
  });

  // 3. Game overlapping a break on same surface
  const breaks = games.filter((g) => g.is_break);
  games.forEach((g) => {
    if (g.is_break) return;
    breaks.forEach((b) => {
      if (String(g.surface) === String(b.surface) && overlap(g, b)) {
        gamesInBreakConflict.add(g.id);
      }
    });
  });

  return {
    conflictsByGameRole,
    conflictsByOfficial,
    gamesInSurfaceConflict,
    gamesInBreakConflict,
    gamesInDuplicate,
    gamesInOfficialConflict,
    surfaceConflictDetails,
  };
}

export function summarizeConflicts(conflicts) {
  return {
    total: Object.keys(conflicts.conflictsByGameRole).length,
    officials: Object.keys(conflicts.conflictsByOfficial).length,
    surface: conflicts.gamesInSurfaceConflict.size,
    breaks: conflicts.gamesInBreakConflict.size,
    duplicates: (conflicts.gamesInDuplicate || new Set()).size,
  };
}
